import {
  getVirtualIpCharacterContext,
  getVirtualIpCharacterSelectionWorkspace,
  getVirtualIpDraft,
  getVirtualIpVoiceWorkspace,
  type VirtualIpCharacterBatch,
  type VirtualIpCharacterSelectionWorkspace,
  type VirtualIpDraft,
  type VirtualIpVoiceWorkspace,
} from '../../api'
import {
  buildVirtualIpWorkflowSnapshot,
  isVirtualIpPositioningConfirmed,
  resolveVirtualIpWorkflowProgress,
  type VirtualIpWorkflowProgress,
} from './virtualIpProgress'

export type VirtualIpWorkflowFacts = {
  draft: VirtualIpDraft
  selectionWorkspace: VirtualIpCharacterSelectionWorkspace | null
  latestBatch: VirtualIpCharacterBatch | null
  voiceWorkspace: VirtualIpVoiceWorkspace | null
  progress: VirtualIpWorkflowProgress
}

/** Load canonical server facts for Virtual IP workflow progress.
 * Safe to call after any mutation that may change unlock/completion state. */
export async function loadVirtualIpWorkflowFacts(
  workspaceId: string,
  existingDraft?: VirtualIpDraft,
  draftId?: string,
): Promise<VirtualIpWorkflowFacts> {
  const draft = existingDraft ?? (await getVirtualIpDraft(workspaceId, draftId)).data
  let selectionWorkspace: VirtualIpCharacterSelectionWorkspace | null = null
  let latestBatch: VirtualIpCharacterBatch | null = null
  let voiceWorkspace: VirtualIpVoiceWorkspace | null = null

  if (isVirtualIpPositioningConfirmed(draft)) {
    try {
      selectionWorkspace = (await getVirtualIpCharacterSelectionWorkspace(workspaceId, draft.id)).data
    } catch {
      try {
        latestBatch = (await getVirtualIpCharacterContext(workspaceId, draft.id)).data.latest_batch
      } catch {
        /* STEP5 may not be ready yet */
      }
    }
    const step6Ready = Boolean(selectionWorkspace?.selection?.selected_id)
    const step5Artifacts = Boolean(selectionWorkspace?.has_any_batch || latestBatch)
    if (step6Ready || step5Artifacts) {
      try {
        voiceWorkspace = (await getVirtualIpVoiceWorkspace(workspaceId, draft.id)).data
      } catch {
        /* STEP7 optional until character path exists */
      }
    }
  }

  const progress = resolveVirtualIpWorkflowProgress(
    buildVirtualIpWorkflowSnapshot({
      draftStatus: draft.status,
      positioningConfirmedAt: draft.positioning_confirmed_at,
      positioningProfileRevision: draft.positioning_profile_revision ?? 0,
      selectionWorkspace,
      latestBatch,
      voiceConfirmed: Boolean(voiceWorkspace?.voice_confirmed),
      voiceReached: Boolean(
        voiceWorkspace?.profile ||
          voiceWorkspace?.latest_batch ||
          (voiceWorkspace?.candidates?.length ?? 0) > 0 ||
          voiceWorkspace?.voice_mode === 'NONE',
      ),
      voiceMode: voiceWorkspace?.voice_mode ?? voiceWorkspace?.profile?.voice_mode ?? null,
      workflowComplete: Boolean(draft.workflow_confirmed_at),
    }),
  )

  return { draft, selectionWorkspace, latestBatch, voiceWorkspace, progress }
}
